import {
  Box,
  Input,
  Spinner,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Text,
  FormControl,
  IconButton,
} from "@chakra-ui/react";
import { Cancel } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useCallback, useEffect, useState } from "react";
import { useInfiniteSearchByPartNumber } from "./hooks/useInfiniteSearchByPartNumber";
import type { TypesenseConfigInput } from "./utils/typesensePartSearch";
import GaraazGeneralPopover from "./GaraazGeneralPopover";
import { GmsPartsSearch } from "../GaraazPartsSearch/GmsPartsSearch";
import { HighlightedSearchText } from "../GaraazPartsSearch/HighlightedSearchText";
import type { PartSearchHit, PartSearchHttpClient } from "../GaraazPartsSearch/types";

export type PartTableRow = {
  _id: string;
  "Part Number": string;
  "Part Name": string;
  orderedQuantity: number;
  brandId: any;
  /** Current editable MRP */
  MRP: number;
  /** Original MRP from part master; never mutated */
  actualMrp?: number;
  discount?: number;
  Brand: string;
  "HSN code": string;
  "Root Part Number": string;
  dateOfPurchase?: string;
  /** Selected batch code for this line item */
  batchCode?: string;
};

interface GaraazPartConversionProps {
  typesenseConfig: TypesenseConfigInput | null;
  partsTableData: PartTableRow[];
  setPartsTableData: React.Dispatch<React.SetStateAction<PartTableRow[]>>;
  quantityZeroError: any[];
  setQuantityZeroError: React.Dispatch<React.SetStateAction<any[]>>;
  setIfManuallyAddedParts: React.Dispatch<React.SetStateAction<boolean>>;
  brandsData?: { _id: string; name: string }[];
  backendSearch?: (partNumber: string, page: number) => Promise<any[]>;
  showDateOfPurchase?: boolean;
  showMrp?: boolean;
  todayStr?: string;
  /** If true, enable MRP editing & validations (for challan) */
  enableMrpValidation?: boolean;
  /** Optional list of allowed batch codes (applied to all rows) */
  possibleBatchCodes?: string[];
  /** If true, every row must have a non-empty batchCode */
  requireBatchCode?: boolean;
  /** If true, show discount input field & with default value 0 */
  showDiscount?: boolean;
  /** Use shared PartsSearch (HTTP API) instead of inline Typesense input. */
  useSharedPartsSearch?: boolean;
  /** Axios client for shared PartsSearch — required when useSharedPartsSearch is true. */
  partSearchAxios?: PartSearchHttpClient;
  /** Optional brand filter for shared PartsSearch (vehicle maker). */
  partSearchBrand?: string;
  /** Emphasize query matches in search results. Default true. */
  enableSearchHighlighting?: boolean;
}

const GaraazPartConversion: React.FC<GaraazPartConversionProps> = ({
  typesenseConfig,
  partsTableData,
  setPartsTableData,
  quantityZeroError,
  setQuantityZeroError,
  setIfManuallyAddedParts,
  brandsData = [],
  backendSearch,
  showDateOfPurchase = false,
  showMrp = false,
  showDiscount = false,
  todayStr = new Date().toISOString().split("T")[0],
  enableMrpValidation = false,
  possibleBatchCodes = [],
  requireBatchCode = false,
  useSharedPartsSearch = false,
  partSearchAxios,
  partSearchBrand,
  enableSearchHighlighting = true,
}) => {
  const [partSearchValue, setPartSearchValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [resultBoxDisplay, setResultBoxDisplay] = useState("none");
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const { parts, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteSearchByPartNumber(
      debouncedValue,
      typesenseConfig,
      20,
      backendSearch,
    );

  const findBrandName = useCallback(
    (brandId: string) => {
      const b = brandsData?.find((x: any) => x._id === brandId);
      return b ? b.name.split(" ")[0] : "";
    },
    [brandsData],
  );

  const [alreadyExistsError, setAlreadyExistsError] = useState<string[]>([]);
  const [mrpErrors, setMrpErrors] = useState<
    { type: "none" | "error" | "warning"; message?: string }[]
  >([]);
  const [batchCodeErrors, setBatchCodeErrors] = useState<string[]>([]);

  // Ensure each row has an immutable actualMrp snapshot initialized once
  useEffect(() => {
    if (!enableMrpValidation || !showMrp) return;

    setPartsTableData((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.actualMrp === undefined && typeof row.MRP === "number") {
          changed = true;
          return { ...row, actualMrp: row.actualMrp ?? row.MRP };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [enableMrpValidation, showMrp, setPartsTableData]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(partSearchValue), 300);
    return () => clearTimeout(t);
  }, [partSearchValue]);

  useEffect(() => {
    setAlreadyExistsError(Array(parts?.length || 0).fill(""));
  }, [parts?.length]);

  useEffect(() => {
    if (!requireBatchCode) {
      setBatchCodeErrors([]);
      return;
    }
    setBatchCodeErrors((prev) => {
      if (prev.length === partsTableData.length) return prev;
      return Array(partsTableData.length).fill("");
    });
  }, [partsTableData.length, requireBatchCode]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLDivElement;
    if (
      el.scrollHeight - el.scrollTop <= el.clientHeight + 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const handlePartClick = (index: number) => {
    if (!parts?.[index]) return;
    addPartFromSearchHit(parts[index], index);
  };

  const addPartFromSearchHit = (p: any, resultIndex?: number) => {
    const exists = partsTableData.some(
      (row) =>
        row["Part Name"] === p.partName &&
        row["Part Number"] === p.partNumber &&
        (row.actualMrp ?? row.MRP) === p.mrp,
    );
    if (exists) {
      if (typeof resultIndex === "number") {
        setAlreadyExistsError((prev) => {
          const n = [...prev];
          n[resultIndex] = "Already Added";
          return n;
        });
      }
      return;
    }
    setPartsTableData((prev) => [
      {
        _id: p._id,
        "Part Name": p.partName,
        "Part Number": p.partNumber,
        brandId: p.brandId,
        orderedQuantity: 1,
        MRP: p.mrp,
        actualMrp: p.mrp,
        discount: p.discount ?? 0,
        Brand: p.brand ?? p.brandName,
        "HSN code": p.hsnCode,
        "Root Part Number": p.rootPartNumber,
        ...(showDateOfPurchase && { dateOfPurchase: todayStr }),
      },
      ...prev,
    ]);
    setQuantityZeroError((prev) => ["", ...prev]);
    setMrpErrors((prev) => [{ type: "none" }, ...prev]);
    if (requireBatchCode) {
      setBatchCodeErrors((prev) => ["", ...prev]);
    }
    setIfManuallyAddedParts(true);
    setResultBoxDisplay("none");
    setPartSearchValue("");
  };

  const handleSharedPartsSearchSelect = (hit: PartSearchHit) => {
    addPartFromSearchHit({
      _id: hit._id,
      partName: hit.partName,
      partNumber: hit.partNumber,
      brandId: hit.brandId,
      mrp: hit.mrp ?? 0,
      brandName: hit.brandName,
      hsnCode: hit.hsnCode,
      rootPartNumber: hit.rootPartNumber,
    });
  };

  const handleDeleteRow = (index: number) => {
    if (partsTableData.length === 1) setIfManuallyAddedParts(false);
    setPartsTableData((prev) => {
      const n = [...prev];
      n.splice(index, 1);
      return n;
    });
    setQuantityZeroError((prev) => {
      const n = [...prev];
      n.splice(index, 1);
      return n;
    });
    if (requireBatchCode) {
      setBatchCodeErrors((prev) => {
        const n = [...prev];
        n.splice(index, 1);
        return n;
      });
    }
  };

  const handleDateOfPurchaseChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const v = e.target.value;
    setPartsTableData((prev) => {
      const n = [...prev];
      n[index] = { ...n[index], dateOfPurchase: v };
      return n;
    });
  };

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const v = e.target.value;
    const num = v === "" ? 0 : parseInt(v, 10);
    setPartsTableData((prev) => {
      const n = [...prev];
      n[index] = { ...n[index], orderedQuantity: num || 0 };
      return n;
    });
    if (v === "" || num <= 0) {
      setQuantityZeroError((prev) => {
        const n = [...prev];
        n[index] = "Quantity must be more than 0";
        return n;
      });
    } else {
      setQuantityZeroError((prev) => {
        const n = [...prev];
        n[index] = "";
        return n;
      });
    }
  };

  const handleBatchCodeChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
    index: number,
  ) => {
    const v = e.target.value;
    setPartsTableData((prev) => {
      const n = [...prev];
      n[index] = { ...n[index], batchCode: v || undefined };
      return n;
    });
    if (requireBatchCode) {
      setBatchCodeErrors((prev) => {
        const n = [...prev];
        n[index] = v.trim() ? "" : "Batch code is required";
        return n;
      });
    }
  };

  const handleMrpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const v = e.target.value;
    const num = v === "" ? NaN : Number(v);
    const baseMrp =
      partsTableData[index]?.actualMrp ?? partsTableData[index]?.MRP ?? 0;

    setPartsTableData((prev) => {
      const n = [...prev];
      n[index] = {
        ...n[index],
        MRP: isNaN(num) ? 0 : num,
      };
      return n;
    });

    if (isNaN(num) || num <= 0) {
      setMrpErrors((prev) => {
        const n = [...prev];
        n[index] = {
          type: "error",
          message: "MRP must be greater than 0",
        };
        return n;
      });
      return;
    }

    if (num < baseMrp) {
      setMrpErrors((prev) => {
        const n = [...prev];
        n[index] = {
          type: "error",
          message: `MRP cannot be less than last MRP (${baseMrp})`,
        };
        return n;
      });
    } else if (num > baseMrp * 1.2) {
      setMrpErrors((prev) => {
        const n = [...prev];
        n[index] = {
          type: "warning",
          message: "MRP seems unusually high compared to the last MRP price.",
        };
        return n;
      });
    } else {
      setMrpErrors((prev) => {
        const n = [...prev];
        n[index] = { type: "none" };
        return n;
      });
    }
  };

  const handleDiscountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const value = e.target.value;

    if (value === "") {
      setPartsTableData((prev) => {
        const n = [...prev];
        // allow empty UI input; default to 0 when sending payload
        n[index] = { ...n[index], discount: undefined };
        return n;
      });
      return;
    }

    const num = Number(value);

    // validate decimal upto 2 digits
    const decimalRegex = /^\d+(\.\d{0,2})?$/;

    if (!decimalRegex.test(value)) {
      return;
    }

    if (num < 0 || num > 100) {
      return;
    }

    setPartsTableData((prev) => {
      const n = [...prev];
      n[index] = { ...n[index], discount: num };
      return n;
    });
  };
  const truncatePartName = (name: string, maxLen = 20) => {
    const str = name ?? "";
    return str.length > maxLen ? `${str.slice(0, maxLen)}...` : str;
  };

  const toPartDetails = (row: PartTableRow) => ({
    "Part Name": row["Part Name"],
    "Part Number": row["Part Number"],
    MRP: row.MRP,
    Brand: findBrandName(row.brandId),
    "HSN code": row["HSN code"],
    "Root Part Number": row["Root Part Number"],
  });

  return (
    <Box width="100%" display="block">
      <Box display="flex" alignItems="center" width="100%">
        <Box position="relative" width={useSharedPartsSearch && partSearchAxios ? "100%" : "50%"}>
          {useSharedPartsSearch && partSearchAxios ? (
            <GmsPartsSearch
              axios={partSearchAxios}
              brand={partSearchBrand}
              onSelect={(hit) => handleSharedPartsSearchSelect(hit)}
              showBrand
              showMrp
              enableHighlighting={enableSearchHighlighting}
              clearOnSelect
            />
          ) : (
            <>
          <FormControl>
            <Input
              value={partSearchValue}
              placeholder="Search By Part Number Or Name"
              onChange={(e) => {
                setPartSearchValue(e.target.value);
                setResultBoxDisplay("flex");
              }}
              size="md"
              borderRadius="5px"
              _placeholder={{ fontSize: "sm" }}
            />
            {isLoading && (
              <Spinner position="absolute" right={2} top={1} size="sm" />
            )}
          </FormControl>
          <Box
            display={resultBoxDisplay}
            flexDirection="column"
            position="absolute"
            width="100%"
            maxH="200px"
            overflowY="auto"
            bg="white"
            border="1px solid lightgrey"
            borderRadius="0 0 5px 5px"
            onScroll={handleScroll}
            zIndex={1}
          >
            <IconButton
              aria-label="close"
              icon={<Cancel fontSize="small" />}
              size="xs"
              position="sticky"
              marginLeft="auto"
              top={1}
              onClick={() => setResultBoxDisplay("none")}
            />
            <Box p={2}>
              {isLoading && <Text>Loading...</Text>}
              {!isLoading &&
                parts?.map((part: any, idx: number) => (
                  <Box
                    key={idx}
                    mb={2}
                    p={2}
                    borderRadius="5px"
                    _hover={{ bg: "#F0F6F6" }}
                    onClick={() => handlePartClick(idx)}
                    cursor="pointer"
                    border="0.5px solid lightgrey"
                  >
                    <Text>
                      {enableSearchHighlighting ? (
                        <HighlightedSearchText
                          text={part.partNumber}
                          query={partSearchValue}
                          highlightSnippet={part._highlightResult?.partNumber?.value}
                        />
                      ) : (
                        part.partNumber
                      )}
                    </Text>
                    <Text noOfLines={1}>
                      {enableSearchHighlighting ? (
                        <HighlightedSearchText
                          text={part.partName}
                          query={partSearchValue}
                          highlightSnippet={part._highlightResult?.partName?.value}
                        />
                      ) : (
                        part.partName
                      )}
                    </Text>
                    {alreadyExistsError[idx] && (
                      <Text color="red" ml="auto">
                        {alreadyExistsError[idx]}
                      </Text>
                    )}
                  </Box>
                ))}
              {isFetchingNextPage && (
                <Box display="flex" justifyContent="center" p={2}>
                  <Spinner size="sm" />
                </Box>
              )}
              {!isLoading && (!parts || parts.length === 0) && (
                <Text>No Options</Text>
              )}
            </Box>
          </Box>
            </>
          )}
        </Box>
      </Box>
      <Box width="100%" mt={3} overflowY="auto" mb={2}>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr borderBottom="1px solid #EDF2F6">
                <Th border="none" width="25%">
                  Part No.
                </Th>
                <Th border="none" width="20%">
                  Part Name
                </Th>
                <Th border="none" isNumeric width="12%" textAlign={"center"}>
                  <Text textAlign={"center"} fontSize="sm">
                    Quantity
                  </Text>
                </Th>
                {showMrp && (
                  <Th border="none" isNumeric width="12%" textAlign={"center"}>
                    <Text textAlign={"center"}>MRP</Text>
                  </Th>
                )}
                {showDiscount && (
                  <Th border="none" isNumeric width="12%" textAlign={"center"}>
                    <Text textAlign={"center"}>Discount (%)</Text>
                  </Th>
                )}
                {possibleBatchCodes.length > 0 && (
                  <Th border="none" width="10%" textAlign={"center"}>
                    <Text textAlign={"center"}>Batch Code</Text>
                  </Th>
                )}
                {showDateOfPurchase && (
                  <Th border="none" width="12%" textAlign={"center"}>
                    <Text textAlign={"center"}>Date of Purchase</Text>
                  </Th>
                )}
                <Th border="none" width="10%" textAlign="end">
                  <Text fontSize="8px" color="#0096e0">
                    {partsTableData.length} Parts Added
                  </Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {partsTableData.map((part, idx) => (
                <Tr
                  key={idx}
                  borderBottom="1px solid #EDF2F6"
                  _hover={{ bg: "#F0F6F6" }}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Td border="none" width="25%">
                    <GaraazGeneralPopover
                      title="Part Details"
                      value={[toPartDetails(part)]}
                      trigger={
                        <Text cursor="pointer" noOfLines={1} fontSize="sm">
                          {part["Part Number"]}
                        </Text>
                      }
                    />
                  </Td>
                  <Td border="none" width="20%">
                    <Text
                      cursor="default"
                      noOfLines={1}
                      fontSize="sm"
                      title={part["Part Name"] ?? ""}
                    >
                      {truncatePartName(part["Part Name"] ?? "", 20)}
                    </Text>
                  </Td>
                  <Td border="none" width="12%" isNumeric textAlign={"center"}>
                    <Box textAlign={"center"}>
                    <Input
                        type="number"
                        size="sm"
                        width={100}
                        min={1}
                        fontSize="sm"
                        lineHeight="1.25rem"
                        value={
                          part.orderedQuantity === 0 ? "" : part.orderedQuantity
                        }
                        onChange={(e) => handleQuantityChange(e, idx)}
                        bg="white"
                        border={"1px solid black"}
                      />
                      {quantityZeroError[idx] && (
                        <Text fontSize="xx-small" color="red">
                          {quantityZeroError[idx]}
                        </Text>
                      )}
                    </Box>
                  </Td>
                  {showMrp && (
                    <Td
                      border="none"
                      width="12%"
                      isNumeric
                      textAlign={"center"}
                    >
                      {enableMrpValidation ? (
                        <Box textAlign={"center"}>
                          <Input
                            type="number"
                            size="sm"
                            width={100}
                            value={Number(part.MRP ?? 0) || ""}
                            onChange={(e) => handleMrpChange(e, idx)}
                            bg="white"
                            border={"1px solid black"}
                          />
                          {mrpErrors[idx]?.type === "error" && (
                            <Text fontSize="xx-small" color="red" mt={1}>
                              {mrpErrors[idx]?.message}
                            </Text>
                          )}
                          {mrpErrors[idx]?.type === "warning" && (
                            <Text fontSize="xx-small" color="orange.400" mt={1}>
                              {mrpErrors[idx]?.message}
                            </Text>
                          )}
                        </Box>
                      ) : (
                        <Text fontSize="sm" textAlign={"center"}>
                          {part.MRP ?? "-"}
                        </Text>
                      )}
                    </Td>
                  )}
                  {showDiscount && (
                    <Td border="none" width="12%" textAlign={"center"}>
                      <Input
                        type="number"
                        size="sm"
                        width={100}
                        min={0}
                        max={100}
                        step="0.01"
                        value={part.discount ?? ""}
                        onChange={(e) => handleDiscountChange(e, idx)}
                        bg="white"
                        border="1px solid black"
                      />
                    </Td>
                  )}
                  {possibleBatchCodes.length > 0 && (
                    <Td border="none" width="10%" textAlign={"center"}>
                      <Input
                        as="select"
                        size="sm"
                        value={part.batchCode ?? ""}
                        onChange={(e) => handleBatchCodeChange(e, idx)}
                        bg="white"
                        padding={0}
                        paddingLeft={2}
                        border={
                          part.batchCode ? "1px solid black" : "1px solid red"
                        }
                      >
                        <option value="">Select...</option>
                        {possibleBatchCodes.map((code) => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </Input>
                      {batchCodeErrors[idx] && (
                        <Text fontSize="xx-small" color="red">
                          {batchCodeErrors[idx]}
                        </Text>
                      )}
                    </Td>
                  )}
                  {showDateOfPurchase && (
                    <Td border="none" width="12%" textAlign={"center"}>
                      <Input
                        type="date"
                        size="sm"
                        value={part.dateOfPurchase ?? todayStr}
                        onChange={(e) => handleDateOfPurchaseChange(e, idx)}
                        bg="white"
                      />
                    </Td>
                  )}
                  <Td
                    border="none"
                    width="10%"
                    textAlign="end"
                    opacity={hoveredIndex === idx ? 1 : 0}
                  >
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDeleteRow(idx)}
                      bg="#BBE6E3"
                      size="xs"
                      _hover={{ bg: "lightgrey" }}
                    >
                      <DeleteIcon style={{ fontSize: "1.3em" }} />
                    </IconButton>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default GaraazPartConversion;
